import { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import PageSubtitle from '@/components/page-subtitle'
import { select } from '@/store'
import { breakpoints, structure, colors } from '@/styles'
import { BulkContext } from '@/pages/Chat/context'
import ChatRoom from './chat-room'
import { ChatProvider } from '../chat-side-bar/chat-context'
import SideBar from '../../../pages/Chat/side-bar'

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${colors.white};
  height: calc(100vh - ${structure.header.height}px);
  position: relative;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: calc(100svh);
    padding-bottom: 0;
  }
`

const BasicSkeleton = ({
  match: {
    params: { listingId, timelineId }
  },
  history,
  location,
  basePath,
  getTimelineSelector,
  sideBarHeader,
  chatFooterButton,
  noTimelinesMessage,
  notSelectedMessage,
  oldDashboardStyle
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const getChatPresets = dispatch.chatPreset.getPresets
  const timelines = useSelector(getTimelineSelector(select, listingId))
  const noTimelines = !timelines.length
  const emptyMessage = noTimelines ? noTimelinesMessage : notSelectedMessage
  const [isOpen, setIsOpen] = useState(!noTimelines)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const timeoutRef = useRef(null)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedChat, setSelectedChat] = useState([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [bulkResult, setBulkResult] = useState([])
  const [navigationState, setNavigationState] = useState(false)
  const maxCount = 50

  useEffect(() => {
    getChatPresets()
  }, [getChatPresets])

  const handleBack = () => {
    setIsTransitioning(true)
    setIsOpen(true)
    timeoutRef.current = setTimeout(() => {
      const pathWithoutTimeline = location.pathname.replace(`/chat/${timelineId}`, '/chat')
      history.replace(pathWithoutTimeline)
      setIsTransitioning(false)
    }, 500)
  }

  useEffect(() => {
    return () => {
      if (typeof timeoutRef.current === 'undefined') return
      clearTimeout(timeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (isTransitioning) return
    if (!timelineId && !isOpen) setIsOpen(true)
    if (!!timelineId & isOpen) setIsOpen(false)
  }, [timelineId, isOpen, isTransitioning])
  const getPath = timelineId => `/${basePath}/${listingId}/chat/${timelineId}`

  return (
    <BulkContext.Provider
      value={{
        selectMode,
        setSelectMode,
        selectedChat,
        setSelectedChat,
        isCheckAll,
        setIsCheckAll,
        bulkResult,
        setBulkResult,
        maxCount,
        navigationState,
        setNavigationState
      }}
    >
      <Helmet>
        <title>{t('common.chat', 'Chat')}</title>
      </Helmet>
      <PageSubtitle>{t('common.chat', 'Chat')}</PageSubtitle>
      <Wrapper oldDashboardStyle={oldDashboardStyle}>
        <ChatProvider value={{ getPath }}>
          <SideBar
            isOpen={isOpen}
            timelines={timelines}
            basePath={basePath}
            sideBarHeader={sideBarHeader}
            chatFooterButton={chatFooterButton}
          />
        </ChatProvider>
        <ChatRoom
          handleBack={handleBack}
          sideBarHeader={sideBarHeader}
          chatFooterButton={chatFooterButton}
          emptyMessage={emptyMessage}
          noTimelines={noTimelines}
          selectMode={selectMode}
        />
      </Wrapper>
    </BulkContext.Provider>
  )
}

export default withRouter(BasicSkeleton)
